export interface CognitoOAuthConfig {
  region: string;
  clientId: string;
  domain: string;
  redirectUri: string;
}

export interface OAuthTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

function getHostedUiBaseUrl(config: CognitoOAuthConfig): string {
  return `https://${config.domain}.auth.${config.region}.amazoncognito.com`;
}

export function getCognitoOAuthConfig(): CognitoOAuthConfig | null {
  const region = import.meta.env.VITE_COGNITO_REGION;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;
  const redirectUri = import.meta.env.VITE_COGNITO_OAUTH_REDIRECT_URI;

  if (!region || !clientId || !domain || !redirectUri) {
    return null;
  }

  return { region, clientId, domain, redirectUri };
}

export function buildAuthorizeUrl(config: CognitoOAuthConfig, connectionToken: string): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email phone',
    redirect_uri: config.redirectUri,
    state: connectionToken,
  });

  return `${getHostedUiBaseUrl(config)}/oauth2/authorize?${params.toString()}`;
}

export function redirectToHostedUi(config: CognitoOAuthConfig, connectionToken: string): void {
  window.location.assign(buildAuthorizeUrl(config, connectionToken));
}

export async function exchangeCodeForTokens(
  config: CognitoOAuthConfig,
  code: string
): Promise<OAuthTokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
  });

  const response = await fetch(`${getHostedUiBaseUrl(config)}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      typeof payload.error_description === 'string'
        ? payload.error_description
        : typeof payload.error === 'string'
          ? payload.error
          : `Token exchange failed: ${response.status}`;
    throw new Error(message);
  }

  if (!payload.access_token) {
    throw new Error('Token exchange did not return an access token');
  }

  return payload as OAuthTokenResponse;
}
